import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LayoutDashboard, Package, PlusCircle, ClipboardList, Users, LogOut, Zap, Copy, Palette, Menu, X, Archive as ArchiveIcon } from "lucide-react";
import AdminNotificationBell from "@/components/shared/AdminNotificationBell";

const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 border-l-2 transition-colors font-body text-sm ${
        isActive
            ? "border-black bg-neutral-50 text-black"
            : "border-transparent text-neutral-500 hover:text-black hover:bg-neutral-50"
    }`;

const NAV_ITEMS = [
    { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/admin/catalog", icon: Package, label: "Catalog", testid: "nav-catalog" },
    { to: "/admin/archive", icon: ArchiveIcon, label: "Archive", testid: "nav-archive" },
    { to: "/admin/add", icon: PlusCircle, label: "Add Product", testid: "nav-add-product" },
    { to: "/admin/bulk", icon: Copy, label: "Bulk Paste", testid: "nav-bulk" },
    { to: "/admin/crawlers", icon: Zap, label: "Crawlers", testid: "nav-crawlers" },
    { to: "/admin/orders", icon: ClipboardList, label: "Orders", testid: "nav-orders" },
    { to: "/admin/designs", icon: Palette, label: "Designs", testid: "nav-designs" },
    { to: "/admin/clients", icon: Users, label: "Clients", testid: "nav-clients" },
];

function NavItems({ onNavigate }) {
    return (
        <>
            {NAV_ITEMS.map((n) => (
                <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    className={linkClass}
                    data-testid={n.testid}
                    onClick={onNavigate}
                >
                    <n.icon className="h-4 w-4" strokeWidth={1.5} /> {n.label}
                </NavLink>
            ))}
        </>
    );
}

function SidebarContent({ user, onNavigate, onLogout }) {
    return (
        <>
            <div className="p-6 border-b border-border flex items-center justify-between">
                <div className="min-w-0">
                    <div className="overline text-neutral-500">Admin Console</div>
                    <div className="font-display text-2xl tracking-tight leading-none mt-1">A V Corp<span className="text-yellow-400">.</span></div>
                </div>
                <AdminNotificationBell />
            </div>
            <nav className="flex-1 py-4 overflow-y-auto">
                <NavItems onNavigate={onNavigate} />
            </nav>
            <div className="p-4 border-t border-border">
                <div className="mb-3">
                    <div className="overline text-neutral-500">Signed in as</div>
                    <div className="font-body text-sm truncate">{user?.name}</div>
                    <div className="font-body text-xs text-neutral-500">ID {user?.login_id}</div>
                </div>
                <Button
                    onClick={onLogout}
                    variant="outline"
                    size="sm"
                    data-testid="logout-btn"
                    className="w-full rounded-none border-black text-black hover:bg-black hover:text-white uppercase text-xs tracking-widest"
                >
                    <LogOut className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} /> Sign out
                </Button>
            </div>
        </>
    );
}

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const doLogout = () => {
        logout();
        nav("/login");
    };

    return (
        <div className="min-h-screen bg-white flex">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex w-64 border-r border-border flex-col shrink-0 sticky top-0 h-screen">
                <SidebarContent user={user} onLogout={doLogout} />
            </aside>

            {/* Mobile top bar */}
            <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-b border-border h-14 flex items-center justify-between px-4">
                <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                    <SheetTrigger asChild>
                        <button
                            className="h-10 w-10 -ml-2 inline-flex items-center justify-center border border-transparent hover:border-black"
                            data-testid="admin-menu-toggle"
                            aria-label="Open menu"
                        >
                            <Menu className="h-5 w-5" strokeWidth={1.5} />
                        </button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-72 p-0 rounded-none border-r border-black flex flex-col">
                        <SidebarContent user={user} onLogout={doLogout} onNavigate={() => setDrawerOpen(false)} />
                    </SheetContent>
                </Sheet>
                <div className="font-display text-lg tracking-tight leading-none">A V Corp<span className="text-yellow-400">.</span></div>
                <div className="flex items-center gap-1">
                    <AdminNotificationBell />
                </div>
            </div>

            <main className="flex-1 min-w-0 pt-14 lg:pt-0">
                <Outlet />
            </main>
        </div>
    );
}
